import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, Database, BarChart3 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const Configuration = () => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sp500Loading, setSp500Loading] = useState(false);
  const [sp500Count, setSp500Count] = useState(0);

  useEffect(() => {
    checkConfiguration();
    loadSP500Count();
  }, []);

  const checkConfiguration = async () => {
    try {
      const response = await axios.get(`${API_BASE}/config`);
      setIsConfigured(response.data.is_configured);
    } catch (error) {
      console.error('Error checking config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSP500Count = async () => {
    try {
      const response = await axios.get(`${API_BASE}/sp500-list`);
      setSp500Count(response.data.length);
    } catch (error) {
      console.error('Error loading S&P 500 count:', error);
    }
  };

  const loadSP500 = async () => {
    setSp500Loading(true);
    try {
      const response = await axios.get(`${API_BASE}/sp500-list`);
      setSp500Count(response.data.length);
      toast.success(`Loaded ${response.data.length} S&P 500 companies`);
    } catch (error) {
      toast.error('Failed to load S&P 500 list');
    } finally {
      setSp500Loading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F1A' }}>
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#3B82F6' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F1A' }}>
      <div className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ color: '#F8FAFC' }}>
          Settings
        </h1>
        <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
          System configuration and data management
        </p>

        {/* System Status Card */}
        <div className="mb-6 p-6 rounded-xl" style={{ backgroundColor: '#0E1324', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5" style={{ color: '#3B82F6' }} />
            <h2 className="text-lg font-semibold" style={{ color: '#F8FAFC' }}>System Status</h2>
          </div>
          
          <div className="flex items-center gap-3 p-4 rounded-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
            <CheckCircle2 
              className="h-6 w-6 flex-shrink-0" 
              style={{ color: isConfigured ? '#4ade80' : '#fbbf24' }} 
            />
            <div>
              <div className="font-medium" style={{ color: '#F8FAFC' }}>
                {isConfigured ? 'Market Data Connected' : 'Initializing...'}
              </div>
              <div className="text-sm" style={{ color: '#94A3B8' }}>
                {isConfigured 
                  ? 'Real-time market data is available' 
                  : 'Connecting to market data services'}
              </div>
            </div>
          </div>
        </div>

        {/* Stock Universe */}
        <div className="p-6 rounded-xl" style={{ backgroundColor: '#0E1324', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Database className="h-5 w-5" style={{ color: '#3B82F6' }} />
            <h2 className="text-lg font-semibold" style={{ color: '#F8FAFC' }}>Stock Universe</h2>
          </div>
          
          <div className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
            <div>
              <div className="font-medium" style={{ color: '#F8FAFC' }}>S&P 500</div>
              <div className="text-sm" style={{ color: '#94A3B8' }}>
                {sp500Count > 0 ? `${sp500Count} companies loaded` : 'Click to load stock list'}
              </div>
            </div>
            <Button
              onClick={loadSP500}
              disabled={sp500Loading}
              variant="outline"
              className="border-blue-500/20 bg-transparent hover:bg-blue-500/10 text-white"
              data-testid="load-sp500-button"
            >
              {sp500Loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : sp500Count > 0 ? (
                'Refresh'
              ) : (
                'Load S&P 500'
              )}
            </Button>
          </div>

          <p className="text-xs mt-4" style={{ color: '#64748B' }}>
            The stock universe determines which companies are analyzed during market scans.
          </p>
        </div>

        {/* Info footer */}
        <div className="mt-8 text-center">
          <p className="text-xs" style={{ color: '#475569' }}>
            Market data provided by secure backend services
          </p>
        </div>
      </div>
    </div>
  );
};

export default Configuration;
